import Vue from "vue";
import Vuex from "vuex";
import grades from "./modules/grades";
import externalLink from "./modules/externalLink";
import notifications from "./modules/notifications";

import {
  Token_EmailEncrypt,
  Token_LogOut,
  Token_SilentSignIn,
  Token_Guid,
  Token_Email,
  Profile_Get,
} from "@/apis/api";
import { rememberAccount } from "@/utils/knownAccounts";
import { clearAccountDrafts, noteSignedInAccount } from "@/utils/accountStorage";
import { isCustomDomain } from "@/utils/custom-domain";

Vue.use(Vuex);

// 全域同步鎖，防止多個組件/路由同時發起 SilentSignIn (重要，避免 Token Rotation 競爭)
// 導出此 Promise 以便 API 攔截器等待
export let syncPromise = null;

// 上一次靜默登入失敗的原因：「unauthorized」= 後端說這張票不能用（真的登出了）；
// 「transient」= 網路斷線、逾時、伺服器 5xx（登入狀態還在，等一下再試）。API 攔截器用它決定要不要把人登出
export let lastSilentSignInFailure = null;

/** access token 是否還能用（沒過期、離過期還有 30 秒以上）。只解 payload 看 exp，不驗簽章 */
const isFreshToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" && payload.exp * 1000 > Date.now() + 30000;
  } catch (e) {
    return false;
  }
};

/*
  跨分頁換票鎖。同一個瀏覽器開了好幾個分頁，access token 一起到期時（隔天打開、電腦睡醒）
  每個分頁都會去換票。同一頁內有 syncPromise 擋，分頁之間沒有 —— 這裡用瀏覽器的 Web Locks 排隊：
  拿到鎖時先看別的分頁是不是已經換好了（localStorage 各分頁共用），換好了就直接用。
  不支援 Web Locks 的瀏覽器（很舊的 Safari）就不鎖，後端有 60 秒寬限可以接住。
*/
const withCrossTabLock = (fn) => {
  try {
    if (typeof navigator !== "undefined" && navigator.locks && typeof navigator.locks.request === "function") {
      return navigator.locks.request("taro-silent-signin", fn);
    }
  } catch (e) {
    /* 不支援就不鎖 */
  }
  return fn();
};

export default new Vuex.Store({
  modules: {
    grades,
    externalLink,
    notifications,
  },
  state: {
    isSerivceRouter: false,
    signinStatus: false,
    signinChecked: false,
    username: null,      // 存 emailEncrypt (hash)
    userRealEmail: null, // 存 raw email (用於表單、顯示)
    userGuid: null,
    userPhoto: "",
    userRealName: "",
    fromSite: {
      en: "",
      name: "",
      pageName: "",
      url: "",
      cmid: "",
    },
    directUrl: "",
    userDevice: null,
    token: null,
    // 登入對話框（components/Common/LoginDialog.vue；開啟用 utils/loginDialog.js）。after：登入完要前往的站內路徑。
    // 切換帳號用的（utils/accountSwitch.js）：email 帶入帳號、blank 不帶入記住的帳密、notice 表單上方的說明、
    // reload 登入完整頁重新載入（關掉沒登入就回首頁）
    loginDialog: { show: false, after: null, view: "SignIn", email: null, blank: false, notice: null, reload: false },
    // 切換帳號對話框（components/Common/AccountSwitchDialog.vue）
    accountSwitcher: false,
    // 對話框登入完留在原頁面時 +1，App.vue 的 router-view 換 key 重建這一頁，讓頁面用登入後的身分重抓資料
    routerViewKey: 0,
  },
  mutations: {
    SetFromSiteCmidConfig(state, fromCmid) {
      if (fromCmid !== undefined) {
        state.fromSite.cmid = fromCmid;
        localStorage.setItem("fromCmid", fromCmid);
      }
    },
    SetFromSiteConfig(state, fromSite) {
      if (!fromSite) {
        fromSite = localStorage.getItem("fromSite") || "lt";
      }
      if (fromSite.toLowerCase() === "sms") {
        state.fromSite.en = "sms";
        state.fromSite.name = "小芋頭社群";
        state.fromSite.pageName = "個人空間頁面";
        state.fromSite.url = process.env.VUE_APP_MAIN_SMS_BASE_URL;
        localStorage.setItem("fromSite", "SMS");
        return;
      }
      if (fromSite.toLowerCase() === "lt") {
        state.fromSite.en = "lt";
        state.fromSite.name = "小芋頭平台";
        state.fromSite.pageName = "小芋頭系統";
        state.fromSite.url = process.env.VUE_APP_MAIN_BASE_URL;
        localStorage.setItem("fromSite", "LT");
        return;
      }
      if (fromSite.toLowerCase() === "wt") {
        state.fromSite.en = "wt";
        state.fromSite.name = "小芋頭工作站";
        state.fromSite.pageName = "小芋頭工作站";
        state.fromSite.url = process.env.VUE_APP_MAIN_WT_BASE_URL;
        localStorage.setItem("fromSite", "WT");
        return;
      }
      if (fromSite.toLowerCase() === "aichat") {
        state.fromSite.en = "aichat";
        state.fromSite.name = "免費AI勞動法務諮詢";
        state.fromSite.pageName = "免費AI勞動法務諮詢";
        state.fromSite.url = process.env.VUE_APP_MAIN_AICHAT_BASE_URL;
        localStorage.setItem("fromSite", "AICHAT");
        return;
      }
      state.fromSite.url = process.env.VUE_APP_MAIN_BASE_URL;
    },
    SetUserPhoto(state, url) {
      state.userPhoto = url;
    },
    OpenLoginDialog(state, payload = {}) {
      state.loginDialog = {
        show: true,
        after: payload.after || null,
        view: payload.view || "SignIn",
        email: payload.email || null,
        blank: !!payload.blank,
        notice: payload.notice || null,
        reload: !!payload.reload,
      };
    },
    SetLoginDialogView(state, view) {
      state.loginDialog = { ...state.loginDialog, view };
    },
    CloseLoginDialog(state) {
      state.loginDialog = { show: false, after: null, view: "SignIn", email: null, blank: false, notice: null, reload: false };
    },
    SetAccountSwitcher(state, show) {
      state.accountSwitcher = !!show;
    },
    BumpRouterView(state) {
      state.routerViewKey += 1;
    },
    SignOut(state) {
      state.username = null;
      state.userRealName = null;
      state.userRealEmail = null;
      state.userPhoto = null;
      state.signinStatus = false;
      state.userGuid = null;
      state.token = null;
      state.signinChecked = true;
      localStorage.removeItem("_token");
      localStorage.removeItem("_signinEmail");
      localStorage.removeItem("_signinUserGuid");
      localStorage.removeItem("_emailEncrypt");
      // 以前沒清：換帳號登入後，付款的買方 email、升級表單、姓名檢查會拿到上一個帳號的 email
      localStorage.removeItem("_rawEmail");
    },
    SetSigninStatus(state, payload) {
      state.signinStatus = payload.status;
      state.signinChecked = true;
      state.username = payload.username || null;
      // 只有在 payload 明確帶有這些欄位時才更新，否則保留現有狀態
      // 這樣 GetEmailEncrypt 呼叫時不會清空頭像/暱稱造成閃爍
      if (payload.userGuid !== undefined) state.userGuid = payload.userGuid || null;
      if (payload.userRealName !== undefined) state.userRealName = payload.userRealName || null;
      if (payload.userPhoto !== undefined) state.userPhoto = payload.userPhoto || null;
      if (payload.userRealEmail !== undefined) state.userRealEmail = payload.userRealEmail || null;
      if (payload.username) {
        localStorage.setItem("_signinEmail", payload.username);
        localStorage.setItem("_emailEncrypt", payload.username); // 恢復相容性 Key，修復全站分享連結
      }
    },
    ClearSigninStatus(state) {
      state.signinStatus = false;
      state.signinChecked = true;
      state.username = null;
      state.userGuid = null;
      state.userRealName = null;
      state.userRealEmail = null;
      state.userPhoto = null;
      localStorage.removeItem("_token");
      localStorage.removeItem("_signinEmail");
      localStorage.removeItem("_signinUserGuid");
      localStorage.removeItem("_emailEncrypt");
      localStorage.removeItem("_rawEmail");
    },
    GetUserDevice(state) {
      var u = navigator.userAgent;
      var ua = navigator.userAgent.toLowerCase();
      state.userDevice = {
        trident: u.indexOf("Trident") > -1,
        presto: u.indexOf("Presto") > -1,
        webKit: u.indexOf("AppleWebKit") > -1,
        gecko: u.indexOf("Gecko") > -1 && u.indexOf("KHTML") == -1,
        mobile: !!u.match(/AppleWebKit.*Mobile.*/),
        ios: !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/),
        android: u.indexOf("Android") > -1 || u.indexOf("Linux") > -1,
        iPhone: u.indexOf("iPhone") > -1,
        iPad: u.indexOf("iPad") > -1,
        webApp: u.indexOf("Safari") == -1,
        iosv: u.substr(u.indexOf("iPhone OS") + 9, 3),
        weixin: ua.match(/MicroMessenger/i) == "micromessenger",
        fbapp: u.indexOf("FBAV") > -1,
        line: u.indexOf("Line") > -1,
      };
    },
  },
  actions: {
    SetFromSiteCmidConfig({ commit }) {
      commit("SetFromSiteCmidConfig");
    },
    SetFromSiteConfig({ commit }) {
      commit("SetFromSiteConfig");
    },
    async GetEmailEncrypt({ commit, dispatch, state }) {
      try {
        const res = await Token_EmailEncrypt();
        if (!res || !res.data) throw new Error("無效的 EmailEncrypt 回應");
        
        const newEmail = res.data;
        const emailChanged = state.username !== newEmail;
        const profileEmpty = !state.userPhoto || !state.userRealName || !state.userGuid;

        commit("SetSigninStatus", {
          status: true,
          username: newEmail,
        });

        if (emailChanged || profileEmpty) {
          dispatch("SyncFullProfile", newEmail);
        }
      } catch (err) {
        console.error("GetEmailEncrypt 失敗:", err);
        // 只有確定沒登入（401 已在攔截器試過換票、400 找不到帳號）才清掉登入狀態。
        // 網路斷線、逾時、伺服器 5xx 先保留 token —— 以前一律登出，網路一閃就要重新登入
        const status = err && err.response && err.response.status;
        if (status === 401 || status === 400 || (err && err.message === "無效的 EmailEncrypt 回應")) {
          await dispatch("SignOut", { callApi: false });
        }
      }
    },
    async SyncFullProfile({ commit, state }, emailEncrypt) {
      try {
        const res_guid = await Token_Guid();
        const res_email = await Token_Email(); 

        if (!res_guid?.data || !res_email?.data) return;

        // 下面「身分沒變」會提早結束，這兩個先寫：付款、升級表單讀它們，要是目前這張票的帳號
        localStorage.setItem("_rawEmail", res_email.data);
        localStorage.setItem("_signinUserGuid", res_guid.data.guid);
        // 跟這台裝置上次登入的帳號不同：清掉上一個帳號留下的升級表單草稿（見 utils/accountStorage.js）
        noteSignedInAccount(res_guid.data.guid);

        if (state.userGuid === res_guid.data.guid && state.userPhoto && state.userRealName) {
           console.log("[Sync] User identity unchanged, skipping UI refresh.");
           return;
        }

        const res_profile = await Profile_Get(res_email.data);
        if (!res_profile?.data?.data) return;

        commit("SetSigninStatus", {
          status: true,
          username: emailEncrypt,
          userGuid: res_guid.data.guid,
          userRealName: res_profile.data.data.name,
          userPhoto: res_profile.data.data.photoUrl,
          userRealEmail: res_email.data,
        });
        localStorage.setItem("_signinEmail", emailEncrypt);
        localStorage.setItem("_emailEncrypt", emailEncrypt); // 恢復相容性 Key

        // 「切換帳號」的清單（utils/knownAccounts.js）。子站自訂網域不記：切換帳號只在平台網域與 App 做
        if (!isCustomDomain()) {
          rememberAccount({
            email: res_email.data,
            name: res_profile.data.data.name,
            photoUrl: res_profile.data.data.photoUrl,
          });
        }
      } catch (err) {
        console.error("SyncFullProfile 失敗:", err);
      }
    },
    GetUserDevice({ commit }) {
      commit("GetUserDevice");
    },
    SetUserPhoto({ commit }, url) {
      commit("SetUserPhoto", url);
    },
    /**
     * callApi = true 是使用者自己按登出（頭像選單、個人中心…）：先解除這支手機的推播（要帶著登入身分，
     * 所以在登出之前），再請後端撤銷登入，最後清掉升級表單草稿。
     * callApi = false 是登入已經失效（被別的裝置登出、刪除帳號…），只清本機的登入狀態。
     * 切換帳號另外走 utils/accountSwitch.js（要確定後端登出成功才繼續）。
     */
    async SignOut({ commit }, payload = { callApi: true }) {
      try {
        if (payload.callApi) {
          // 動態載入：push.js 會引用 router，router 又引用 store
          const { unregisterPush } = await import("@/utils/push");
          await unregisterPush();
          await Token_LogOut();
        }
      } catch (err) {
        console.warn("後端登出 API 呼叫失敗或略過", err);
      } finally {
        commit("SignOut");
        if (payload.callApi) clearAccountDrafts();
        return true;
      }
    },
    async SilentSignIn({ commit, dispatch }) {
      if (syncPromise) return syncPromise;

      // 【防死鎖設計】
      // syncPromise 只保護 Token Rotation 這一步（呼叫 silent-signin）
      // 取到新 Token 後立刻釋放鎖，讓後續的 GetEmailEncrypt（使用 normalRequest）
      // 不會被自己的 syncPromise 擋住，避免死鎖。
      let resolveSync;
      syncPromise = new Promise((res) => {
        resolveSync = res;
      });
      const tokenBefore = localStorage.getItem("_token");

      try {
        const token = await withCrossTabLock(async () => {
          // 排隊等鎖的時候，別的分頁可能已經換到新票：直接用它，不用再換一次
          const current = localStorage.getItem("_token");
          if (current && current !== tokenBefore && isFreshToken(current)) return current;
          const res = await Token_SilentSignIn(); // 使用 authRequest，不受鎖影響
          return res && res.data && res.data.token;
        });

        if (token) {
          localStorage.setItem("_token", token);
          lastSilentSignInFailure = null;
          // ✅ 新 Token 已寫入 localStorage，立刻釋放鎖
          // 讓後續所有等待中的 normalRequest 拿到最新 token 後繼續
          syncPromise = null;
          resolveSync(true);

          // 在鎖釋放後，才執行需要 normalRequest 的個人資料同步
          // 加上 await 確保 vuex 的 signinStatus 成功設定後再往回跑，防止 router 在拿到 Token 但狀態還沒改的空窗期就把人踢出去
          await dispatch("GetEmailEncrypt");
          return true;
        } else {
          syncPromise = null;
          resolveSync(false);
          lastSilentSignInFailure = "unauthorized";
          commit("ClearSigninStatus");
          return false;
        }
      } catch (err) {
        syncPromise = null;
        resolveSync(false);
        // 只有後端明確回 401（票過期、撤銷、已被用掉）才清掉登入狀態；
        // 網路斷線、逾時、伺服器 5xx 先保留，下次再試
        if (err && err.response && err.response.status === 401) {
          lastSilentSignInFailure = "unauthorized";
          commit("ClearSigninStatus");
        } else {
          lastSilentSignInFailure = "transient";
        }
        return false;
      }
    },
  },
});
